import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { FileDownload, Print } from "@mui/icons-material";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DynamicTable from "../../../../components/lab-dynamic-table";
import DeleteConfirmationModal from "../../../../components/Modals/delete-modal";

const ParameterType: React.FC<any> = () => {
  const [editData, setEditData] = useState<any>(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState<any>("");
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState("");

  const [formData, setFormData] = useState({
    parameterType: "",
    description: "",
  });

  const [errors, setErrors] = useState({
    parameterType: false,
  });

  const [parameterTypeData, setParameterTypeData] = useState<any>();

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchInput), 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const fetchParameterTypes = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/parameter-type/getAllByPagination`,
        {
          params: {
            pageNo: page + 1,
            count: rowsPerPage,
            search: debouncedSearch,
          },
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (res?.data.isSuccess) {
        setParameterTypeData(res.data?.data);
        setTotal(res.data?.totalCount);
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching parameter types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParameterTypes();
  }, [page, rowsPerPage, debouncedSearch]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user types
    if (errors[name as keyof typeof errors] !== undefined) {
      setErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    const newErrors = {
      parameterType: !formData.parameterType,
    };

    setErrors(newErrors);

    // Check if there are any errors
    if (Object.values(newErrors).some((error) => error)) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      if (editData) {
        await axios.patch(
          `${process.env.REACT_APP_LAB_URL}/parameter-type/UpdateById/${editData}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        toast.success("Parameter type updated successfully");
      } else {
        await axios.post(
          `${process.env.REACT_APP_LAB_URL}/parameter-type/addParameterType`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        toast.success("Parameter type added successfully");
      }
      fetchParameterTypes();
      setEditData(null);
      setFormData({
        parameterType: "",
        description: "",
      });
      setErrors({
        parameterType: false,
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error.message);
    }
  };

  const handleEditClick = (item: any) => {
    setEditData(item._id);
    setFormData({
      parameterType: item.parameterType,
      description: item.description || "",
    });
    setErrors({
      parameterType: false,
    });
  };

  const handleDeleteClick = (id: string) => {
    setIsDeleteModalOpen(true);
    setSelectedItemId(id);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_LAB_URL}/parameter-type/RemoveById/${selectedItemId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (response?.data.isSuccess) {
        toast.success(response?.data.message);
      }
      fetchParameterTypes();
      setEditData(null);
      setFormData({
        parameterType: "",
        description: "",
      });
      setErrors({
        parameterType: false,
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  // Excel Import functionality
  const handleExcelFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (!["xls", "xlsx"].includes(fileExtension || "")) {
      toast.error("Please upload a valid Excel file (.xls or .xlsx)");
      e.target.value = "";
      return;
    }

    const data = new FormData();
    data.append("file", file);

    try {
      setUploading(true);

      const res = await axios.post(
        `${process.env.REACT_APP_LAB_URL}/import-data/fileUpload`,
        data,
        {
          params: { entity: "parameterType" },
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success(res.data?.message || "File uploaded successfully");
      fetchParameterTypes();

      e.target.value = "";
    } catch (error: any) {
      console.error("❌ Error uploading excel file", error);
      toast.info(
        error.response?.data?.message ||
          error.message ||
          "Failed to upload file"
      );
      e.target.value = "";
    } finally {
      setUploading(false);
    }
  };

  // Export to PDF function
  const exportToPDF = () => {
    if (!parameterTypeData?.length) return toast.warn("No data to export.");

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 8;
    let yPosition = margin;

    // Header
    doc
      .setFontSize(14)
      .setFont("helvetica", "bold")
      .setTextColor(25, 55, 85)
      .text("PARAMETER TYPE REPORT", pageWidth / 2, yPosition, {
        align: "center",
      });

    yPosition += 6;
    doc
      .setDrawColor(25, 55, 85)
      .setLineWidth(0.5)
      .line(margin, yPosition, pageWidth - margin, yPosition);

    // Metadata
    yPosition += 5;
    doc
      .setFontSize(8)
      .setFont("helvetica", "normal")
      .setTextColor(80, 80, 80)
      .text(
        `Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
        pageWidth / 2,
        yPosition,
        { align: "center" }
      );

    yPosition += 8;

    const head = [["SN", "Parameter Type", "Description"]];

    const body = parameterTypeData.map((item: any, index: number) => [
      index + 1,
      item.parameterType || "",
      item.description || "",
    ]);

    autoTable(doc, {
      startY: yPosition,
      head,
      body,
      headStyles: {
        fillColor: [25, 55, 85],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 8,
        cellPadding: 3,
      },
      bodyStyles: {
        fontSize: 7,
        textColor: [40, 40, 40],
        cellPadding: 2,
        valign: "middle",
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250],
      },
      margin: { top: margin, right: margin, bottom: margin, left: margin },
      didDrawPage: function (data) {
        const pageSize = doc.internal.pageSize;
        const pageCount = doc.getNumberOfPages();
        const pageNum = data.pageNumber;

        doc.setFontSize(6);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Page ${pageNum} of ${pageCount}`,
          pageSize.getWidth() / 2,
          pageSize.getHeight() - 4,
          { align: "center" }
        );
      },
    });

    doc.output("dataurlnewwindow");
  };

  // Export to Excel function
  const exportToExcel = () => {
    if (!parameterTypeData?.length) return toast.warn("No data to export.");

    const worksheetData = parameterTypeData.map((item: any, index: number) => ({
      SN: index + 1,
      "Parameter Type": item.parameterType || "",
      Description: item.description || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ParameterTypes");
    XLSX.writeFile(workbook, "ParameterTypes.xlsx");
  };

  return (
    <Box>
      <Box
        sx={{
          borderRadius: 2,
          padding: "1px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
          }}
        >
          Parameter Type
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<FileDownload />}
            onClick={exportToExcel}
            sx={{
              minWidth: { xs: "auto", md: "80px" },
              fontSize: { xs: "12px", md: "12px" },
              fontWeight: "600",
              textTransform: "none",
              borderRadius: "8px",
              background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
              color: "#ffffff",
              boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
              transition: "all 0.3s ease-in-out",
              "&:hover": {
                background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
                boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
                transform: "translateY(-1px)",
              },
            }}
          >
            Excel
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Print />}
            onClick={exportToPDF}
            sx={{
              minWidth: { xs: "auto", md: "80px" },
              fontSize: { xs: "12px", md: "12px" },
              fontWeight: "600",
              textTransform: "none",
              borderRadius: "8px",
              background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
              color: "#ffffff",
              boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
              transition: "all 0.3s ease-in-out",
              "&:hover": {
                background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
                boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
                transform: "translateY(-1px)",
              },
            }}
          >
            Print
          </Button>
          <TextField
            label="Search"
            placeholder="Search parameter types..."
            variant="outlined"
            className="custom-input"
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              endAdornment: (
                <IconButton>
                  <SearchIcon sx={{ color: "#055FFC" }} />
                </IconButton>
              ),
            }}
          />
        </Box>
      </Box>

      <Grid container spacing={1}>
        <Grid item xs={12} sm={4}>
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              px: 3,
              py: 1,
              boxShadow: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              {editData ? "Update Parameter Type" : "Add New Parameter Type"}
            </Typography>

            <TextField
              label="Parameter Type"
              name="parameterType"
              value={formData.parameterType}
              fullWidth
              size="small"
              onChange={handleChange}
              className="custom-input"
              required
              error={errors.parameterType}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Description"
              name="description"
              value={formData.description}
              fullWidth
              size="small"
              multiline
              rows={3}
              onChange={handleChange}
              className="custom-input"
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSubmit}
            >
              {editData ? "Update" : "Save"}
            </Button>

            <div className="w-full mt-4 flex justify-between items-center gap-2">
              <div>
                <input
                  type="file"
                  accept=".xls,.xlsx"
                  id="excel-upload"
                  onChange={handleExcelFileUpload}
                  style={{ display: "none" }}
                />
                <label htmlFor="excel-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    disabled={uploading}
                    startIcon={
                      uploading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <img
                          src="/excel-logo.png"
                          alt="Excel File"
                          style={{ width: 24, height: 24 }}
                        />
                      )
                    }
                    sx={{
                      textTransform: "none",
                      borderColor: uploading ? "#ccc" : "#217346",
                      color: uploading ? "#ccc" : "#217346",
                      "&:hover": {
                        borderColor: uploading ? "#ccc" : "#1b5e38",
                        backgroundColor: uploading ? "transparent" : "#f0f8f4",
                      },
                    }}
                  >
                    {uploading ? "Uploading..." : "Import Excel File (.xls/.xlsx)"}
                  </Button>
                </label>
              </div>
              <Tooltip arrow placement="top" title="Download Excel Template">
                <IconButton
                  sx={{
                    color: "white",
                    "&:hover": {},
                  }}
                  onClick={() => {
                    const link = document.createElement("a");
                    link.href = "/Parameter type.xlsx";
                    link.download = "Parameter type.xlsx";
                    link.click();
                  }}
                >
                  <img
                    src="/export-excel.png"
                    alt="Excel Template"
                    style={{ width: 30, height: 30 }}
                  />
                </IconButton>
              </Tooltip>
            </div>
          </Box>
        </Grid>

        <Grid item xs={12} sm={8}>
          <DynamicTable
            headers={["SN", "Parameter Type", "Description", "Actions"]}
            data={parameterTypeData?.map((item: any, index: number) => [
              page * rowsPerPage + index + 1,
              item.parameterType || "",
              item.description || "",
            ])}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={(_e, newPage) => setPage(newPage)}
            handleChangeRowsPerPage={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            totalRows={total}
            actionButtons={[
              {
                icon: <EditIcon sx={{ color: "#FFB800", cursor: "pointer" }} />,
                onClick: (rowIndex: any) =>
                  handleEditClick(parameterTypeData[rowIndex]),
              },
              {
                icon: (
                  <DeleteIcon sx={{ color: "#FF5454", cursor: "pointer" }} />
                ),
                onClick: (rowIndex: any) =>
                  handleDeleteClick(parameterTypeData[rowIndex]?._id),
              },
            ]}
            height={"60vh"}
          />
        </Grid>
      </Grid>

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        handleClose={() => setIsDeleteModalOpen(false)}
        handleConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default ParameterType;
